/**
 * @file shared/desktop/useDesktopSession.ts
 * @brief A live session page tells the desktop widget what is happening and
 *        takes its commands (DESKTOP/src/widget.js, 02-10-2026).
 *
 * @details
 * In a browser tab, or an older desktop shell, this does nothing. The page
 * stays the only owner of the session: the widget is a view and a remote, so
 * nothing here can start, stop or restart a recording on its own.
 *
 * Publishing: on any change except the clock, plus every 5 s to keep the
 * widget's local timer honest. `at` must be when `elapsedMs` was READ (the
 * page sets it): a hidden window's animation frames stop, so its clock can be
 * stale, and re-stamping `at` here would make the widget's clock jump back. `null` when the session ends or the page
 * unmounts, which hides the widget.
 *
 * @author DES
 * @date 02-10-2026
 */

import { useEffect, useRef } from "react";
import { desktopShell, type DesktopCommand, type DesktopSession } from "./desktopShell";

export function useDesktopSession(
  state: DesktopSession | null,
  onCommand: (cmd: DesktopCommand) => void,
): void {
  const shell = desktopShell();
  const canPublish = !!shell?.publishSession;

  const latest = useRef(state);
  latest.current = state;
  const commandRef = useRef(onCommand);
  commandRef.current = onCommand;

  // Everything but the clock: the widget runs its own from `elapsedMs` + `at`.
  const { elapsedMs: _e, at: _a, ...rest } = state ?? ({} as Partial<DesktopSession>);
  const key = state ? JSON.stringify(rest) : "null";

  useEffect(() => {
    if (!canPublish) return;
    shell!.publishSession!(latest.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPublish, key]);

  useEffect(() => {
    if (!canPublish) return;
    const id = window.setInterval(() => {
      const s = latest.current;
      if (s) shell!.publishSession!(s);
    }, 5000);
    return () => {
      window.clearInterval(id);
      shell!.publishSession!(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPublish]);

  useEffect(() => {
    if (!shell?.onSessionCommand) return;
    return shell.onSessionCommand((cmd) => commandRef.current(cmd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!shell?.onSessionCommand]);
}
